import { useEffect, useMemo, useRef, useState } from 'react';
import { SignaturePad } from '../../components/SignaturePad';
import { getAuthToken } from '../../lib/api';
import {
  useDenikPhotos,
  useUploadDenikPhoto,
  useDenikAttachments,
  useUploadDenikAttachment,
  useDeleteDenikAttachment,
  useDenikWorkers,
  useAddDenikWorker,
  useRemoveDenikWorker,
  useGeocodeStavba,
  useCompanies,
} from '../../api/hooks';
import { fetchWeather } from './weather';
import { CompanyPickerButton, type CompanyPickResult } from '../../components/ares';
import type {
  DenikDataJson,
  DenikEntry,
  DenikMaterialEntry,
  DenikStrojEntry,
  DenikWorker,
  DenikWorkerFav,
} from '../../api/schemas';
import styles from './DenikDayForm.module.css';

const PODMINKY = [
  { v: '', l: '— vyber —' },
  { v: 'jasno', l: '☀️ Jasno' },
  { v: 'polojasno', l: '⛅ Polojasno' },
  { v: 'zatazeno', l: '☁️ Zataženo' },
  { v: 'dest', l: '🌧️ Déšť' },
  { v: 'snih', l: '🌨️ Sníh' },
  { v: 'mraz', l: '❄️ Mráz' },
  { v: 'bourkovy', l: '⛈️ Bouřka' },
];

const VITR = ['', 'slabý', 'mírný', 'silný', 'velmi silný'];

export const MACHINES = [
  'Bagr', 'Nakladač', 'Autojeřáb', 'Věžový jeřáb',
  'Nákladní auto', 'Domíchávač', 'Čerpadlo betonu', 'Míchačka',
  'Vibrátor', 'Lešení', 'Plošina', 'Kompresor',
];

/** Typické stavební pozice (předvyplnění v datalistu — uživatel může napsat libovolnou). */
const POZICE_PRESETY = [
  'Stavbyvedoucí',
  'Mistr',
  'Dělník',
  'Pomocný dělník',
  'Tesař',
  'Zedník',
  'Železář',
  'Betonář',
  'Elektrikář',
  'Instalatér',
  'Klempíř',
  'Pokrývač',
  'Obkladač',
  'Sádrokartonář',
  'Malíř',
  'Štukatér',
  'Bagrista',
  'Jeřábník',
  'Řidič',
  'Strojník',
];

export function emptyData(): DenikDataJson {
  return {
    pocasi: { teplota: '', podminky: '', vitr: '' },
    pracovnici: [],
    prace: [],
    prace_text: '',
    materialy: [],
    stroje: MACHINES.map((nazev) => ({ nazev, pouzit: false })),
    stroje_jine: '',
    udalosti: '',
    podpis_stavbyvedouci: '',
    podpis_tdi: '',
    podpis_investor: '',
    sig_sv: '',
    sig_tdi: '',
    sig_inv: '',
    sig_stamp_sv: '',
    sig_stamp_tdi: '',
    sig_stamp_inv: '',
  };
}

/** Stará strukturovaná data prací → jeden volný text (po řádcích). */
function flattenPraceArray(arr: DenikDataJson['prace']): string {
  if (!arr || arr.length === 0) return '';
  return arr
    .map((p) => {
      const main = (p.popis ?? '').trim();
      const qty = (p.mnozstvi ?? '').trim();
      const unit = (p.jednotka ?? '').trim();
      const tail = qty || unit ? ` — ${qty}${unit ? ' ' + unit : ''}`.trimEnd() : '';
      return main ? `• ${main}${tail}` : '';
    })
    .filter(Boolean)
    .join('\n');
}

export function mergeData(incoming: DenikDataJson | undefined): DenikDataJson {
  const base = emptyData();
  if (!incoming) return base;
  const strojeMap = new Map<string, boolean>();
  (incoming.stroje ?? []).forEach((s) => strojeMap.set(s.nazev, !!s.pouzit));

  // Provedené práce: pokud máme nový text, použij. Jinak flatni starou strukturu (pokud existuje).
  const praceTextRaw = (incoming.prace_text ?? '').trim();
  const praceTextResolved = praceTextRaw || flattenPraceArray(incoming.prace);

  return {
    ...base,
    ...incoming,
    pocasi: { ...base.pocasi, ...(incoming.pocasi ?? {}) },
    pracovnici: incoming.pracovnici ?? [],
    prace: incoming.prace ?? [],
    prace_text: praceTextResolved,
    materialy: incoming.materialy ?? [],
    stroje: base.stroje!.map((s) => ({ nazev: s.nazev, pouzit: strojeMap.get(s.nazev) ?? false })),
    stroje_jine: incoming.stroje_jine ?? '',
    udalosti: incoming.udalosti ?? '',
  };
}

/** Auth-aware obrázek (JWT v hlavičce, blob URL). */
function PhotoThumb({ url }: { url: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const token = getAuthToken();
    let blobUrl: string | null = null;
    let cancelled = false;
    fetch(url, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then((b) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(b);
        setSrc(blobUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [url]);
  if (!src) return null;
  return <img src={src} alt="" />;
}

function workerKey(w: { jmeno?: string; pozice?: string; firma?: string }): string {
  return [w.jmeno ?? '', w.pozice ?? '', w.firma ?? ''].join('|').toLowerCase();
}

export interface DenikDayFormProps {
  stavbaId: number;
  /** Pokud je null, fotky/oblíbení nelze ukládat — formulář pracuje jen v paměti. */
  entry: DenikEntry | null;
  /** Datum dne ve formátu YYYY-MM-DD — pro auto-počasí. */
  isoDate: string;
  data: DenikDataJson;
  onChange: (next: DenikDataJson) => void;
  disabled?: boolean;
}

export function DenikDayForm({ stavbaId, entry, isoDate, data, onChange, disabled }: DenikDayFormProps) {
  const photosQuery = useDenikPhotos(stavbaId, entry?.id ?? null);
  const uploadPhoto = useUploadDenikPhoto(stavbaId, entry?.id ?? null);

  const attachmentsQuery = useDenikAttachments(stavbaId, entry?.id ?? null);
  const uploadAttachment = useUploadDenikAttachment(stavbaId, entry?.id ?? null);
  const deleteAttachment = useDeleteDenikAttachment(stavbaId, entry?.id ?? null);

  const favWorkersQuery = useDenikWorkers(stavbaId);
  const addFavWorker = useAddDenikWorker(stavbaId);
  const removeFavWorker = useRemoveDenikWorker(stavbaId);

  // Companies pro autocomplete v poli "Firma" pracovníka.
  const companiesQuery = useCompanies();
  const firmaSuggestions = useMemo(() => {
    const set = new Set<string>();
    (companiesQuery.data ?? []).forEach((c) => {
      const n = c.nazev?.trim();
      if (n) set.add(n);
    });
    // Plus už použité firmy z dnešního zápisu (ať se i ručně psaná hodnota objeví v ostatních řádcích).
    (data.pracovnici ?? []).forEach((w) => {
      const n = w.firma?.trim();
      if (n) set.add(n);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'cs'));
  }, [companiesQuery.data, data.pracovnici]);

  // Pozice = presety + uživatelské pozice již použité v dnešním zápisu.
  const poziceSuggestions = useMemo(() => {
    const set = new Set<string>(POZICE_PRESETY);
    (data.pracovnici ?? []).forEach((w) => {
      const p = w.pozice?.trim();
      if (p) set.add(p);
    });
    return Array.from(set);
  }, [data.pracovnici]);

  const geocode = useGeocodeStavba(stavbaId);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const attachInputRef = useRef<HTMLInputElement | null>(null);
  const [wxStatus, setWxStatus] = useState<{ kind: 'idle' | 'loading' | 'ok' | 'err'; msg?: string }>({ kind: 'idle' });

  async function handleAutoWeather() {
    setWxStatus({ kind: 'loading' });
    try {
      const geo = await geocode.mutateAsync();
      const lat = parseFloat(geo.latitude);
      const lon = parseFloat(geo.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
        setWxStatus({ kind: 'err', msg: 'Stavbě chybí adresa pro geokódování.' });
        return;
      }
      const wx = await fetchWeather(lat, lon, isoDate);
      if (!wx) {
        setWxStatus({ kind: 'err', msg: 'Počasí pro tento den nelze stáhnout.' });
        return;
      }
      onChange({
        ...data,
        pocasi: { teplota: wx.teplota, podminky: wx.podminky, vitr: wx.vitr },
      });
      setWxStatus({ kind: 'ok', msg: '✓ Počasí načteno' });
      window.setTimeout(() => setWxStatus((s) => (s.kind === 'ok' ? { kind: 'idle' } : s)), 3000);
    } catch (e) {
      setWxStatus({
        kind: 'err',
        msg: e instanceof Error ? e.message : 'Chyba při načítání počasí.',
      });
    }
  }

  function setPocasi<K extends keyof NonNullable<DenikDataJson['pocasi']>>(k: K, v: string) {
    onChange({ ...data, pocasi: { ...(data.pocasi ?? {}), [k]: v } });
  }

  function setWorker(i: number, patch: Partial<DenikWorker>) {
    const ps = [...(data.pracovnici ?? [])];
    ps[i] = { ...ps[i], ...patch };
    onChange({ ...data, pracovnici: ps });
  }
  function addWorker(prefill?: Partial<DenikWorker>) {
    onChange({
      ...data,
      pracovnici: [
        ...(data.pracovnici ?? []),
        { jmeno: '', pozice: '', firma: '', pocet: 1, hodin: 8, ...(prefill ?? {}) },
      ],
    });
  }
  function delWorker(i: number) {
    onChange({ ...data, pracovnici: (data.pracovnici ?? []).filter((_, j) => j !== i) });
  }

  function setMat(i: number, patch: Partial<DenikMaterialEntry>) {
    const arr = [...(data.materialy ?? [])];
    arr[i] = { ...arr[i], ...patch };
    onChange({ ...data, materialy: arr });
  }
  function addMat() {
    onChange({ ...data, materialy: [...(data.materialy ?? []), { nazev: '', mnozstvi: '', dodavatel: '' }] });
  }
  function delMat(i: number) {
    onChange({ ...data, materialy: (data.materialy ?? []).filter((_, j) => j !== i) });
  }

  function toggleStroj(nazev: string, on: boolean) {
    onChange({
      ...data,
      stroje: (data.stroje ?? []).map((s: DenikStrojEntry) =>
        s.nazev === nazev ? { ...s, pouzit: on } : s,
      ),
    });
  }

  const favorites = favWorkersQuery.data ?? [];
  const currentWorkerKeys = useMemo(
    () => new Set((data.pracovnici ?? []).map(workerKey)),
    [data.pracovnici],
  );

  function toggleFavoriteToList(fav: DenikWorkerFav) {
    const k = workerKey(fav);
    if (currentWorkerKeys.has(k)) {
      onChange({
        ...data,
        pracovnici: (data.pracovnici ?? []).filter((w) => workerKey(w) !== k),
      });
    } else {
      addWorker({
        jmeno: fav.jmeno,
        pozice: fav.pozice ?? '',
        firma: fav.firma ?? '',
        pocet: fav.pocet ?? 1,
        hodin: fav.hodin ?? 8,
      });
    }
  }

  async function saveWorkerAsFavorite(w: DenikWorker) {
    if (!w.jmeno?.trim()) return;
    const k = workerKey(w);
    if (favorites.find((f) => workerKey(f) === k)) return;
    try {
      await addFavWorker.mutateAsync({
        jmeno: w.jmeno.trim(),
        pozice: w.pozice ?? '',
        firma: w.firma ?? '',
        pocet: w.pocet ?? 1,
        hodin: w.hodin ?? 8,
      });
    } catch {}
  }

  async function removeFavorite(id: number) {
    try { await removeFavWorker.mutateAsync(id); } catch {}
  }

  async function handlePhotoUpload(files: FileList | null, fromCamera: boolean) {
    if (!files || !files.length || !entry) return;
    for (const file of Array.from(files)) {
      try { await uploadPhoto.mutateAsync(file); } catch {}
    }
    const ref = fromCamera ? cameraInputRef.current : fileInputRef.current;
    if (ref) ref.value = '';
  }

  async function handleAttachmentUpload(files: FileList | null) {
    if (!files || !files.length || !entry) return;
    for (const file of Array.from(files)) {
      try { await uploadAttachment.mutateAsync(file); } catch {}
    }
    if (attachInputRef.current) attachInputRef.current.value = '';
  }

  async function openAttachment(att: { id: number; mime_type: string; filename: string }) {
    if (!entry) return;
    const token = getAuthToken();
    const r = await fetch(
      `/api/stavby/${stavbaId}/denik/${entry.id}/attachments/${att.id}/content`,
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
    );
    if (!r.ok) return;
    const b = await r.blob();
    window.open(URL.createObjectURL(b), '_blank');
  }

  async function handleDeleteAttachment(attId: number, filename: string) {
    if (!window.confirm(`Smazat přílohu „${filename}"?`)) return;
    try { await deleteAttachment.mutateAsync(attId); } catch {}
  }

  function fmtSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function fileIcon(mime: string): string {
    if (mime.startsWith('image/')) return '🖼️';
    if (mime === 'application/pdf') return '📄';
    if (mime.includes('word')) return '📝';
    if (mime.includes('excel') || mime.includes('spreadsheet')) return '📊';
    if (mime.startsWith('video/')) return '🎬';
    return '📎';
  }

  async function openPhoto(id: number) {
    const token = getAuthToken();
    const r = await fetch(
      `/api/stavby/${stavbaId}/photos/${id}/full`,
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
    );
    if (!r.ok) return;
    const b = await r.blob();
    window.open(URL.createObjectURL(b), '_blank');
  }

  const photos = photosQuery.data ?? [];

  return (
    <div className={styles.form}>
      {/* Datalisty pro autocomplete v polích Pozice / Firma pracovníka. */}
      <datalist id="denik-pozice-list">
        {poziceSuggestions.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>
      <datalist id="denik-firma-list">
        {firmaSuggestions.map((f) => (
          <option key={f} value={f} />
        ))}
      </datalist>

      {disabled && <div className={styles.lockedNote}>Zápis je uzamčený — nelze upravovat.</div>}

      {/* Počasí */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionTitle}>Počasí</div>
          <div className={styles.sectionActions}>
            {wxStatus.kind === 'loading' && (
              <span className={styles.wxStatus}>Stahuju počasí…</span>
            )}
            {wxStatus.kind === 'ok' && (
              <span className={`${styles.wxStatus} ${styles.wxStatusOk}`}>{wxStatus.msg}</span>
            )}
            {wxStatus.kind === 'err' && (
              <span className={`${styles.wxStatus} ${styles.wxStatusErr}`}>{wxStatus.msg}</span>
            )}
            <button
              type="button"
              className={styles.wxBtn}
              onClick={handleAutoWeather}
              disabled={disabled || wxStatus.kind === 'loading' || geocode.isPending}
              title="Stáhnout počasí z Open-Meteo (přepíše aktuální hodnoty)"
            >
              🌤️ Načíst počasí
            </button>
          </div>
        </div>
        <div className={styles.grid3}>
          <div className={styles.field}>
            <label className={styles.label}>Teplota (°C)</label>
            <input
              className={styles.input}
              type="text"
              inputMode="numeric"
              value={data.pocasi?.teplota ?? ''}
              onChange={(e) => setPocasi('teplota', e.target.value)}
              disabled={disabled}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Podmínky</label>
            <select
              className={styles.select}
              value={data.pocasi?.podminky ?? ''}
              onChange={(e) => setPocasi('podminky', e.target.value)}
              disabled={disabled}
            >
              {PODMINKY.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Vítr</label>
            <select
              className={styles.select}
              value={data.pocasi?.vitr ?? ''}
              onChange={(e) => setPocasi('vitr', e.target.value)}
              disabled={disabled}
            >
              {VITR.map((v) => <option key={v} value={v}>{v || '— vyber —'}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* Pracovníci */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionTitle}>Pracovníci</div>
          <div className={styles.sectionActions}>
            <span className={styles.favHint}>
              ★ uloží do oblíbených · klik na chip přidá do dne
            </span>
          </div>
        </div>

        {favorites.length > 0 ? (
          <div className={styles.favRow}>
            {favorites.map((f) => {
              const inList = currentWorkerKeys.has(workerKey(f));
              return (
                <span
                  key={f.id}
                  className={`${styles.favChip} ${inList ? styles.favChipInList : ''}`}
                  onClick={() => !disabled && toggleFavoriteToList(f)}
                  title={inList ? 'Klikni pro odebrání ze seznamu' : 'Klikni pro přidání do dnešního seznamu'}
                >
                  {inList ? '✓' : '+'} {f.jmeno}{f.firma ? ` · ${f.firma}` : ''}
                  <button
                    type="button"
                    className={styles.favChipDel}
                    onClick={(e) => { e.stopPropagation(); removeFavorite(f.id); }}
                    disabled={disabled}
                    title="Odebrat z oblíbených"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        ) : (
          <div className={styles.favEmpty}>
            Tip: vyplňte pracovníka a klikněte na ★ — uloží se do oblíbených a v dalších dnech ho přidáte jedním klikem.
          </div>
        )}

        <div className={styles.rows}>
          {(data.pracovnici ?? []).map((w, i) => {
            const k = workerKey(w);
            const isFav = !!favorites.find((f) => workerKey(f) === k);
            return (
              <div key={i} className={styles.rowWorker}>
                <input className={styles.input} placeholder="Jméno"
                  value={w.jmeno ?? ''} onChange={(e) => setWorker(i, { jmeno: e.target.value })} disabled={disabled} />
                <input className={styles.input} placeholder="Pozice" list="denik-pozice-list"
                  value={w.pozice ?? ''} onChange={(e) => setWorker(i, { pozice: e.target.value })} disabled={disabled} />
                <input className={styles.input} placeholder="Firma" list="denik-firma-list"
                  value={w.firma ?? ''} onChange={(e) => setWorker(i, { firma: e.target.value })} disabled={disabled} />
                <input className={styles.input} type="number" min={1} placeholder="Počet"
                  value={w.pocet ?? ''} onChange={(e) => setWorker(i, { pocet: Number(e.target.value) || 0 })} disabled={disabled} />
                <input className={styles.input} type="number" min={0} step={0.5} placeholder="Hod"
                  value={w.hodin ?? ''} onChange={(e) => setWorker(i, { hodin: Number(e.target.value) || 0 })} disabled={disabled} />
                <button
                  type="button"
                  className={`${styles.saveBtn} ${isFav ? styles.saveBtnOn : ''}`}
                  onClick={() => saveWorkerAsFavorite(w)}
                  disabled={disabled || isFav || !w.jmeno?.trim()}
                  title={isFav ? 'Pracovník je v oblíbených' : 'Uložit pracovníka do oblíbených'}
                  aria-label={isFav ? 'Uloženo' : 'Uložit do oblíbených'}
                >
                  {isFav ? '✓' : '💾'}
                </button>
                <button type="button" className={styles.iconBtn} onClick={() => delWorker(i)} disabled={disabled} title="Odebrat">×</button>
              </div>
            );
          })}
          <div className={styles.addRowGroup}>
            <button type="button" className={styles.addRow} onClick={() => addWorker()} disabled={disabled}>
              + Přidat pracovníka
            </button>
            <CompanyPickerButton
              compact
              label="Z adresáře"
              title="Načíst pracovníka z adresáře firem/kontaktů"
              disabled={disabled}
              onPick={(c: CompanyPickResult) => {
                addWorker({
                  jmeno: c.kontakt_jmeno || c.stavbyvedouci || '',
                  pozice: c.stavbyvedouci ? 'stavbyvedoucí' : '',
                  firma: c.nazev || '',
                  pocet: 1,
                  hodin: 8,
                });
              }}
            />
          </div>
        </div>
      </section>

      {/* Provedené práce — volný text */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionTitle}>Provedené práce</div>
        </div>
        <textarea
          className={styles.textarea}
          value={data.prace_text ?? ''}
          onChange={(e) => onChange({ ...data, prace_text: e.target.value })}
          placeholder="Co se dnes dělalo… (např. betonáž základů — 8 m³, vyzdění příčky 1.NP, montáž oken jihozápadní fasády)"
          disabled={disabled}
          rows={5}
        />
      </section>

      {/* Materiály */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionTitle}>Dodané materiály</div>
        </div>
        <div className={styles.rows}>
          {(data.materialy ?? []).map((m, i) => (
            <div key={i} className={styles.row3}>
              <input className={styles.input} placeholder="Materiál"
                value={m.nazev} onChange={(e) => setMat(i, { nazev: e.target.value })} disabled={disabled} />
              <input className={styles.input} placeholder="Množství"
                value={m.mnozstvi ?? ''} onChange={(e) => setMat(i, { mnozstvi: e.target.value })} disabled={disabled} />
              <input className={styles.input} placeholder="Dodavatel"
                value={m.dodavatel ?? ''} onChange={(e) => setMat(i, { dodavatel: e.target.value })} disabled={disabled} />
              <button type="button" className={styles.iconBtn} onClick={() => delMat(i)} disabled={disabled}>×</button>
            </div>
          ))}
          <button type="button" className={styles.addRow} onClick={addMat} disabled={disabled}>+ Přidat materiál</button>
        </div>
      </section>

      {/* Stroje */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionTitle}>Použitá mechanizace</div>
        </div>
        <div className={styles.machines}>
          {(data.stroje ?? []).map((s) => (
            <label key={s.nazev} className={styles.machineItem}>
              <input
                type="checkbox"
                checked={s.pouzit}
                onChange={(e) => toggleStroj(s.nazev, e.target.checked)}
                disabled={disabled}
              />
              {s.nazev}
            </label>
          ))}
        </div>
        <div className={styles.field} style={{ marginTop: 6 }}>
          <label className={styles.label}>Jiné stroje</label>
          <input
            className={styles.input}
            value={data.stroje_jine ?? ''}
            onChange={(e) => onChange({ ...data, stroje_jine: e.target.value })}
            disabled={disabled}
          />
        </div>
      </section>

      {/* Události */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionTitle}>Události, zjištění, problémy</div>
        </div>
        <textarea
          className={styles.textarea}
          value={data.udalosti ?? ''}
          onChange={(e) => onChange({ ...data, udalosti: e.target.value })}
          placeholder="Zápisy o průběhu stavby, nestandardní události, rozhodnutí…"
          disabled={disabled}
        />
      </section>

      {/* Fotky */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionTitle}>Fotky dne</div>
        </div>
        {!entry ? (
          <div className={styles.photoEmpty}>Fotky lze nahrát po prvním uložení dne.</div>
        ) : (
          <>
            {photosQuery.isLoading ? (
              <div className={styles.photoEmpty}>Načítám fotky…</div>
            ) : photos.length === 0 ? (
              <div className={styles.photoEmpty}>Zatím žádné fotky.</div>
            ) : (
              <div className={styles.photoGrid}>
                {photos.map((p) => (
                  <div
                    key={p.id}
                    className={styles.photoThumb}
                    title={p.popis || p.filename}
                    onClick={() => openPhoto(p.id)}
                  >
                    <PhotoThumb url={`/api/stavby/${stavbaId}/photos/${p.id}/thumbnail`} />
                  </div>
                ))}
              </div>
            )}
            <div className={styles.uploadRow}>
              <label className={styles.photoUpload}>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  disabled={disabled || uploadPhoto.isPending}
                  onChange={(e) => handlePhotoUpload(e.target.files, true)}
                />
                📷 Vyfotit
              </label>
              <label className={styles.photoUpload}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={disabled || uploadPhoto.isPending}
                  onChange={(e) => handlePhotoUpload(e.target.files, false)}
                />
                🖼️ Z galerie
              </label>
              {uploadPhoto.isPending && <span className={styles.uploadingNote}>Nahrávám…</span>}
            </div>
          </>
        )}
      </section>

      {/* Přílohy — PDF, dokumenty… */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionTitle}>Přílohy (PDF, dokumenty)</div>
        </div>
        {!entry ? (
          <div className={styles.photoEmpty}>Přílohy lze nahrát po prvním uložení dne.</div>
        ) : (
          <>
            {(() => {
              const list = Array.isArray(attachmentsQuery.data) ? attachmentsQuery.data : [];
              if (attachmentsQuery.isLoading) {
                return <div className={styles.photoEmpty}>Načítám přílohy…</div>;
              }
              if (attachmentsQuery.error) {
                return (
                  <div className={styles.photoEmpty}>
                    Přílohy se nepodařilo načíst.{' '}
                    {attachmentsQuery.error instanceof Error ? attachmentsQuery.error.message : ''}
                  </div>
                );
              }
              if (list.length === 0) {
                return <div className={styles.photoEmpty}>Zatím žádné přílohy.</div>;
              }
              return (
                <div className={styles.attachmentList}>
                  {list.map((att) => (
                    <div key={att.id} className={styles.attachmentItem}>
                      <button
                        type="button"
                        className={styles.attachmentMain}
                        onClick={() => openAttachment(att)}
                        title="Otevřít přílohu"
                      >
                        <span className={styles.attachmentIcon}>{fileIcon(att.mime_type)}</span>
                        <span className={styles.attachmentName}>{att.filename}</span>
                        <span className={styles.attachmentMeta}>{fmtSize(att.size)}</span>
                      </button>
                      <button
                        type="button"
                        className={styles.iconBtn}
                        onClick={() => handleDeleteAttachment(att.id, att.filename)}
                        disabled={disabled || deleteAttachment.isPending}
                        title="Smazat přílohu"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className={styles.uploadRow}>
              <label className={styles.photoUpload}>
                <input
                  ref={attachInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/*,image/*"
                  multiple
                  disabled={disabled || uploadAttachment.isPending}
                  onChange={(e) => handleAttachmentUpload(e.target.files)}
                />
                📎 Nahrát přílohu
              </label>
              {uploadAttachment.isPending && <span className={styles.uploadingNote}>Nahrávám…</span>}
            </div>
          </>
        )}
      </section>

      {/* Podpisy */}
      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionTitle}>Jmenovky a podpisy</div>
        </div>
        <div className={styles.signatureRow}>
          {([
            { key: 'sv', label: 'Stavbyvedoucí', nameField: 'podpis_stavbyvedouci', sigField: 'sig_sv', stampField: 'sig_stamp_sv' },
            { key: 'tdi', label: 'TDI / TDS', nameField: 'podpis_tdi', sigField: 'sig_tdi', stampField: 'sig_stamp_tdi' },
            { key: 'inv', label: 'Investor', nameField: 'podpis_investor', sigField: 'sig_inv', stampField: 'sig_stamp_inv' },
          ] as const).map((s) => (
            <div key={s.key} className={styles.signatureBlock}>
              <label className={styles.label}>{s.label}</label>
              <input
                className={styles.input}
                placeholder="Jméno"
                value={(data[s.nameField] as string) ?? ''}
                onChange={(e) => onChange({ ...data, [s.nameField]: e.target.value })}
                disabled={disabled}
              />
              <SignaturePad
                value={(data[s.sigField] as string) ?? ''}
                onChange={(v) => onChange({ ...data, [s.sigField]: v })}
                disabled={disabled}
                hint="Podepsat prstem / myší"
                width={280}
                height={80}
              />
              {(data[s.stampField] as string) ? (
                <div className={styles.stampNote}>{(data[s.stampField] as string)}</div>
              ) : null}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
